/**
 * InvoiceValidationSettingsSection Component
 *
 * Invoice validation settings — the AI validation first (switch + editable brief), then the
 * standard rule validation (always on, with the linked-supplier requirement) and the payment
 * security checks. Only the brief needs a save; every switch and mode is stored the moment it
 * changes.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useRef, useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  ConfirmationModal,
  ExpandableTextarea,
  InputWrapper,
  Loader,
  Paper,
  Select,
  Toggle
} from '@insio/ui-toolkit';
import {
  AutomationKind,
  DEFAULT_IS_LINKED_SUPPLIER_REQUIRED,
  SupplierBankAccountCheck
} from '@insio/shared-types';
import {
  InvoiceAutomationSettingsDocument,
  InvoiceValidationSettingsDocument,
  PreviewInvoiceValidationDocument,
  ResetInvoiceValidationSettingsDocument,
  UpdateInvoiceAutomationSettingsDocument,
  UpdateInvoiceValidationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { AutomationHelpModal } from './AutomationHelpModal';
import { AutomationStepHeading } from './AutomationStepHeading';
import { InvoicePickerModal } from './InvoicePickerModal';
import { ValidationPreviewResult } from './ValidationPreviewResult';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

// Derived from the enum the backend registers into the schema — a new member shows up
// here, and its missing translation key fails the i18n tests. `OFF` is the switch, not a mode.
const BANK_ACCOUNT_CHECK_MODES = Object.values(SupplierBankAccountCheck).filter(
  mode => mode !== SupplierBankAccountCheck.OFF
);

/** What switching the check on selects. */
const BANK_ACCOUNT_CHECK_DEFAULT = SupplierBankAccountCheck.USED_OR_PUBLISHED;

/** Modes that ask a tax register — one exists only for CZ/SK suppliers. */
const REGISTER_MODES: SupplierBankAccountCheck[] = [
  SupplierBankAccountCheck.USED_OR_PUBLISHED,
  SupplierBankAccountCheck.PUBLISHED_ONLY
];

/** Everything the settings row holds besides the brief. */
interface Switches {
  isEnabled: boolean;
  supplierBankAccountCheck: SupplierBankAccountCheck;
  isUnreliablePayerCheckEnabled: boolean;
}

export function InvoiceValidationSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.validation');
  const tHelp = useFeatureTranslation('systemSettings.automation.help');
  const { showSuccess, showError } = useToastHelpers();
  // Whoever only reads the rules sees every choice locked, with no save, dry-run or restore.
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(InvoiceValidationSettingsDocument);
  // The responses carry no id to normalise, so the settings query is refetched —
  // otherwise the dirty check keeps comparing against the pre-save snapshot.
  const settingsRefetch = {
    refetchQueries: [{ query: InvoiceValidationSettingsDocument }],
    awaitRefetchQueries: true
  };
  const [updateSettings] = useMutation(UpdateInvoiceValidationSettingsDocument, settingsRefetch);
  const [resetSettings, { loading: isResetting }] = useMutation(
    ResetInvoiceValidationSettingsDocument,
    settingsRefetch
  );
  const [runPreview, { data: previewData, loading: isPreviewing, error: previewError }] =
    useMutation(PreviewInvoiceValidationDocument);
  const {
    data: automationData,
    loading: automationLoading,
    error: automationError
  } = useQuery(InvoiceAutomationSettingsDocument);
  const [updateAutomationSettings] = useMutation(UpdateInvoiceAutomationSettingsDocument, {
    refetchQueries: [{ query: InvoiceAutomationSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [prompt, setPrompt] = useState('');
  // The policy as last stored; an instant write refetches the settings and must not wipe a draft.
  const storedPrompt = useRef<string | null>(null);
  const [switches, setSwitches] = useState<Switches>({
    isEnabled: false,
    supplierBankAccountCheck: SupplierBankAccountCheck.OFF,
    isUnreliablePayerCheckEnabled: false
  });
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isResultDismissed, setIsResultDismissed] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // Only the policy save is shown on the button; instant writes never touch its state.
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isLinkedSupplierRequired, setIsLinkedSupplierRequired] = useState(
    DEFAULT_IS_LINKED_SUPPLIER_REQUIRED
  );
  const [isSavingLinkedSupplier, setIsSavingLinkedSupplier] = useState(false);

  const settings = data?.invoiceValidationSettings;
  const automationSettings = automationData?.invoiceAutomationSettings;

  useEffect(() => {
    if (automationSettings) {
      setIsLinkedSupplierRequired(automationSettings.isLinkedSupplierRequired);
    }
  }, [automationSettings]);

  useEffect(() => {
    if (settings) {
      // Read now: the updater may run after the ref below already holds the new brief.
      const previous = storedPrompt.current;
      setPrompt(prev => (previous === null || prev === previous ? settings.prompt : prev));
      storedPrompt.current = settings.prompt;
      setSwitches({
        isEnabled: settings.isEnabled,
        supplierBankAccountCheck: settings.supplierBankAccountCheck,
        isUnreliablePayerCheckEnabled: settings.isUnreliablePayerCheckEnabled
      });
    }
  }, [settings]);

  const isPromptDirty = settings != null && prompt !== settings.prompt;
  const isBusy = isSavingPrompt || isResetting;
  // `OFF` is what the switch writes, so the stored mode stays the single source of truth.
  const isBankAccountCheckEnabled =
    switches.supplierBankAccountCheck !== SupplierBankAccountCheck.OFF;

  /** Writes one whole settings row. */
  const persist = async (next: Switches & { prompt: string }): Promise<boolean> => {
    try {
      await updateSettings({ variables: { input: next }, errorPolicy: 'none' });
      return true;
    } catch {
      showError(t('saveError'));
      return false;
    }
  };

  const handleSavePrompt = async () => {
    if (!settings || !canChange) return;
    setIsSavingPrompt(true);
    try {
      if (await persist({ ...switches, prompt })) {
        showSuccess(t('saveSuccess'));
      }
    } finally {
      setIsSavingPrompt(false);
    }
  };

  /** A switch or mode is stored the moment it changes; a refused write puts it back. */
  const handleSwitchesChange = async (change: Partial<Switches>) => {
    if (!settings || !canChange) return;
    const next = { ...switches, ...change };
    setSwitches(next);
    if (!(await persist({ ...next, prompt: settings.prompt }))) {
      setSwitches(switches);
    }
  };

  /** Stored the moment it flips, sent alone; a refused write puts it back. */
  const handleLinkedSupplierChange = async (isRequired: boolean) => {
    if (!canChange) return;
    setIsLinkedSupplierRequired(isRequired);
    setIsSavingLinkedSupplier(true);
    try {
      await updateAutomationSettings({
        variables: { input: { isLinkedSupplierRequired: isRequired } },
        errorPolicy: 'none'
      });
    } catch {
      setIsLinkedSupplierRequired(!isRequired);
      showError(t('saveError'));
    } finally {
      setIsSavingLinkedSupplier(false);
    }
  };

  const handlePreview = async (invoiceId: string) => {
    if (!canChange) return;
    setIsResultDismissed(false);
    try {
      await runPreview({ variables: { invoiceId } });
    } catch {
      // Surfaced by the result panel via previewError — the mutation error is the single source.
    }
  };

  const handleReset = async () => {
    if (!canChange) return;
    try {
      const { data: resetData } = await resetSettings();
      const restored = resetData?.resetInvoiceValidationSettings;
      if (restored) {
        setPrompt(restored.prompt);
      }
      setIsResetConfirmOpen(false);
      showSuccess(t('resetSuccess'));
    } catch {
      setIsResetConfirmOpen(false);
      showError(t('resetError'));
    }
  };

  if ((loading && !settings) || (automationLoading && !automationSettings)) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <div className='space-y-lg'>
      <AutomationStepHeading title={t('title')} description={t('description')} />

      <Paper heading='stripe' bleed padding='none' testId='validation-ai-section'>
        <Paper.Title>
          <h2>{t('ai.title')}</h2>
        </Paper.Title>
        <Paper.Action>
          <Toggle
            checked={switches.isEnabled}
            onChange={isEnabled => handleSwitchesChange({ isEnabled })}
            disabled={!canChange}
            testId='validation-ai-enabled-toggle'
          />
        </Paper.Action>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('ai.description')}</p>
          <div className='flex flex-col gap-md'>
            <InputWrapper label={t('ai.prompt.label')} className='gap-xs flex-1 min-w-0'>
              <ExpandableTextarea
                label={t('ai.prompt.label')}
                rows={8}
                value={prompt}
                onChange={setPrompt}
                disabled={!canChange}
                isStretch
              />
            </InputWrapper>
          </div>

          <div className='flex flex-wrap items-center gap-sm'>
            {canChange && (
              <>
                <Button
                  variant='primary'
                  onClick={handleSavePrompt}
                  disabled={!isPromptDirty || isBusy}
                  testId='validation-settings-save'
                >
                  {isSavingPrompt ? t('saving') : t('save')}
                </Button>
                <Button
                  variant='secondary'
                  onClick={() => setIsPickerOpen(true)}
                  disabled={isPromptDirty || isBusy || isPreviewing}
                  title={isPromptDirty ? t('preview.unsavedChanges') : undefined}
                  testId='validation-preview-open'
                >
                  {isPreviewing ? t('preview.running') : t('preview.title')}
                </Button>
                <Button
                  variant='ghost'
                  onClick={() => setIsResetConfirmOpen(true)}
                  disabled={isBusy}
                  testId='validation-settings-reset'
                >
                  {isResetting ? t('resetting') : t('reset')}
                </Button>
              </>
            )}
            <Button
              variant='ghost'
              icon='circle-info'
              onClick={() => setIsHelpOpen(true)}
              testId='validation-help-open'
            >
              {tHelp('open')}
            </Button>
          </div>

          {!isResultDismissed && (
            <ValidationPreviewResult
              isRunning={isPreviewing}
              hasError={previewError != null}
              result={previewData?.previewInvoiceValidation}
              onDismiss={() => setIsResultDismissed(true)}
            />
          )}
        </Paper.Content>
      </Paper>

      <Paper heading='stripe' bleed padding='none' testId='validation-standard-section'>
        <Paper.Title>
          <h2>{t('standard.title')}</h2>
        </Paper.Title>
        <Paper.Action>
          <Badge color='neutral' size='sm'>
            {t('standard.badge')}
          </Badge>
        </Paper.Action>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('standard.description')}</p>
          {automationError ? (
            <p
              className='text-sm text-error-text'
              data-testid='validation-linked-supplier-load-error'
            >
              {t('loadError')}
            </p>
          ) : (
            <InputWrapper
              label={t('standard.linkedSupplier.title')}
              hint={t('standard.linkedSupplier.description')}
              className='gap-xs'
            >
              <Toggle
                checked={isLinkedSupplierRequired}
                disabled={!canChange || isSavingLinkedSupplier}
                onChange={handleLinkedSupplierChange}
                testId='validation-linked-supplier-toggle'
              />
            </InputWrapper>
          )}
        </Paper.Content>
      </Paper>

      <Paper heading='stripe' bleed padding='none' testId='validation-payment-security-section'>
        <Paper.Title>
          <h2>{t('paymentSecurity.title')}</h2>
        </Paper.Title>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('paymentSecurity.description')}</p>
          <InputWrapper
            label={t('paymentSecurity.unreliablePayer.title')}
            hint={t('paymentSecurity.unreliablePayer.description')}
            className='gap-xs'
          >
            <Toggle
              checked={switches.isUnreliablePayerCheckEnabled}
              onChange={isUnreliablePayerCheckEnabled =>
                handleSwitchesChange({ isUnreliablePayerCheckEnabled })
              }
              disabled={!canChange}
              testId='validation-payment-security-unreliable-payer-toggle'
            />
          </InputWrapper>

          <InputWrapper
            label={t('paymentSecurity.bankAccount.title')}
            className='gap-xs border-t border-border-base pt-md'
          >
            <Toggle
              checked={isBankAccountCheckEnabled}
              onChange={isOn =>
                handleSwitchesChange({
                  supplierBankAccountCheck: isOn
                    ? BANK_ACCOUNT_CHECK_DEFAULT
                    : SupplierBankAccountCheck.OFF
                })
              }
              disabled={!canChange}
              testId='validation-payment-security-bank-account-toggle'
            />
          </InputWrapper>
          {isBankAccountCheckEnabled && (
            <>
              <InputWrapper
                label={t('paymentSecurity.bankAccount.label')}
                hint={t(
                  `paymentSecurity.bankAccount.options.${switches.supplierBankAccountCheck}.description`
                )}
                className='gap-xs max-w-150'
              >
                <Select
                  value={switches.supplierBankAccountCheck}
                  onChange={value =>
                    handleSwitchesChange({
                      supplierBankAccountCheck: value as SupplierBankAccountCheck
                    })
                  }
                  options={BANK_ACCOUNT_CHECK_MODES.map(option => ({
                    value: option,
                    label: t(`paymentSecurity.bankAccount.options.${option}.label`)
                  }))}
                  disabled={!canChange}
                  testId='validation-payment-security-bank-account-select'
                />
              </InputWrapper>
              {REGISTER_MODES.includes(switches.supplierBankAccountCheck) && (
                <AlertBanner
                  variant='info'
                  title={t('paymentSecurity.bankAccount.foreignNotice')}
                  testId='validation-payment-security-bank-account-foreign-notice'
                />
              )}
            </>
          )}
        </Paper.Content>
      </Paper>

      <ConfirmationModal
        open={isResetConfirmOpen}
        title={t('resetConfirm.title')}
        message={t('resetConfirm.message')}
        confirmLabel={t('resetConfirm.confirm')}
        cancelLabel={t('resetConfirm.cancel')}
        variant='warning'
        loading={isResetting}
        onConfirm={handleReset}
        onClose={() => setIsResetConfirmOpen(false)}
        testId='validation-reset-confirm'
      />

      <AutomationHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        kind={AutomationKind.INVOICE_VALIDATION}
        title={t('title')}
      />

      <InvoicePickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handlePreview}
        title={t('preview.title')}
        description={t('preview.description')}
        testId='validation-preview-picker'
      />
    </div>
  );
}
